import type { BusRoute, BusStop, CommuteAnchor, CommuteRecommendation, Coordinate, Direction, LiveEvent, NearbyStop, NotificationDashboard, RouteSnapshot, StopSearchResult, TrackingRule, TrackingRuleInput, UserPreferences, UserProfile } from "@bus/shared";

export const API_BASE = import.meta.env.VITE_API_BASE || "/api";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { ...init, credentials: "include", headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers } });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { message?: string; error?: string; errorCode?: string } | null;
    const error = new Error(body?.message || body?.error || `API request failed (${response.status})`) as Error & { status?: number; code?: string };
    error.status = response.status; error.code = body?.errorCode;
    throw error;
  }
  return response.json() as Promise<T>;
}

export const getRoutes = () => api<BusRoute[]>("/v1/routes");
export interface RouteStaticData { route: BusRoute; stops: BusStop[]; shape: Coordinate[] }
export const getRouteStatic = (routeId: string, direction: Direction) => api<RouteStaticData>(`/v1/routes/${encodeURIComponent(routeId)}?direction=${direction}`);
export const getSnapshot = (routeId: string, direction: Direction) => api<RouteSnapshot>(`/v1/routes/${encodeURIComponent(routeId)}/snapshot?direction=${direction}`);
export async function getMe(): Promise<UserProfile | null> {
  const response = await fetch(`${API_BASE}/v1/me`, { credentials: "include", headers: { accept: "application/json" } });
  if (response.status === 204) return null;
  if (!response.ok) throw new Error(`Profile request failed (${response.status})`);
  return response.json() as Promise<UserProfile>;
}
export interface AuthConfig { mode: "legacy" | "google"; googleClientId: string | null }
export const getAuthConfig = () => api<AuthConfig>("/v1/auth/config");
export const signInWithGoogle = (credential: string) => api<UserProfile>("/v1/auth/google", { method: "POST", body: JSON.stringify({ credential }) });
export const logout = () => api<{ ok: true }>("/v1/auth/logout", { method: "POST", body: "{}" });
export const savePreferences = (preferences: UserPreferences) => api<UserProfile>("/v1/me/preferences", { method: "PATCH", body: JSON.stringify(preferences) });
export interface NearbyResponse { latitude: number; longitude: number; radiusMeters: number; stops: NearbyStop[]; generatedAt: string }
export const getNearbyStops = (latitude: number, longitude: number, radius = 1000, limit = 5) => api<NearbyResponse>(`/v1/nearby/stops?lat=${latitude}&lon=${longitude}&radius=${radius}&limit=${limit}`);
export const setFavorite = (routeId: string, favorite: boolean) => api<{ favoriteRouteIds: string[] }>(`/v1/me/favorites/${encodeURIComponent(routeId)}`, { method: "PUT", body: JSON.stringify({ favorite }) });
export const searchStops = (query: string) => api<StopSearchResult[]>(`/v1/stops/search?q=${encodeURIComponent(query)}&limit=20`);
export const saveCommuteAnchor = (anchor: CommuteAnchor) => api<CommuteAnchor[]>(`/v1/me/commute-anchors/${anchor.kind}`, { method: "PUT", body: JSON.stringify(anchor) });
export const getCommuteRecommendations = (latitude: number, longitude: number) => api<{ detectedAnchor: "home" | "work" | null; recommendations: CommuteRecommendation[] }>(`/v1/me/commute-recommendations?lat=${latitude}&lon=${longitude}`);

export type LiveConnectionStatus = "connecting" | "connected" | "disconnected";

export function openLiveStream(routeId: string, direction: Direction, onEvent: (event: LiveEvent) => void, onStatus?: (status: LiveConnectionStatus) => void): () => void {
  const stream = new EventSource(`${API_BASE}/v1/live?routeId=${encodeURIComponent(routeId)}&direction=${direction}`, { withCredentials: true });
  onStatus?.("connecting");
  stream.onopen = () => onStatus?.("connected");
  stream.onerror = () => onStatus?.("disconnected");
  stream.onmessage = (message) => onEvent(JSON.parse(message.data) as LiveEvent);
  return () => stream.close();
}

export interface DeviceCredentials { deviceId: string; deviceToken: string }
const storageKey = "bus-now-device";

export async function getOrCreateDevice(): Promise<DeviceCredentials> {
  const saved = localStorage.getItem(storageKey);
  if (saved) return JSON.parse(saved) as DeviceCredentials;
  const credentials = await api<DeviceCredentials>("/v1/devices", { method: "POST", body: JSON.stringify({ timezone: "Asia/Taipei" }) });
  localStorage.setItem(storageKey, JSON.stringify(credentials));
  return credentials;
}

export async function claimCurrentDevice(): Promise<UserProfile | null> {
  const credentials = await getOrCreateDevice();
  try {
    return await api<UserProfile>("/v1/me/devices/claim", { method: "POST", body: JSON.stringify({ deviceId: credentials.deviceId, deviceToken: credentials.deviceToken }) });
  } catch { return null; }
}

const authHeaders = (credentials: DeviceCredentials) => ({ authorization: `Bearer ${credentials.deviceToken}`, "x-device-id": credentials.deviceId });

export async function savePushSubscription(subscription: PushSubscriptionJSON): Promise<void> {
  const credentials = await getOrCreateDevice();
  try {
    await api(`/v1/me/devices/${credentials.deviceId}/push`, { method: "PUT", body: JSON.stringify({ pushSubscription: subscription, enabled: true }) });
  } catch {
    await api(`/v1/devices/${credentials.deviceId}/push`, { method: "PUT", headers: authHeaders(credentials), body: JSON.stringify({ pushSubscription: subscription }) });
  }
}

export async function listTrackingRules(): Promise<TrackingRule[]> {
  const credentials = await getOrCreateDevice();
  return api("/v1/tracking-rules", { headers: authHeaders(credentials) });
}

export async function createTrackingRule(input: TrackingRuleInput): Promise<TrackingRule> {
  const credentials = await getOrCreateDevice();
  return api("/v1/tracking-rules", { method: "POST", headers: authHeaders(credentials), body: JSON.stringify(input) });
}

export async function updateTrackingRule(id: string, input: TrackingRuleInput): Promise<TrackingRule> {
  const credentials = await getOrCreateDevice();
  return api(`/v1/tracking-rules/${id}`, { method: "PUT", headers: authHeaders(credentials), body: JSON.stringify(input) });
}

export async function deleteTrackingRule(id: string): Promise<void> {
  const credentials = await getOrCreateDevice();
  await api(`/v1/tracking-rules/${id}`, { method: "DELETE", headers: authHeaders(credentials) });
}

export async function getNotificationDashboard(deviceId: string): Promise<NotificationDashboard> {
  return api(`/v1/me/notification-dashboard?deviceId=${encodeURIComponent(deviceId)}`);
}

export async function testCurrentPush(deviceId: string): Promise<{ ok: true; sentAt: string }> {
  return api(`/v1/me/devices/${encodeURIComponent(deviceId)}/push/test`, { method: "POST", body: "{}" });
}

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

export async function enablePush(): Promise<PushSubscription> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("此裝置不支援 Web Push");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("通知權限未開啟");
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const publicKey = String(import.meta.env.VITE_VAPID_PUBLIC_KEY || "");
  if (!publicKey) throw new Error("尚未設定 VAPID public key");
  const subscription = existing ?? await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  await savePushSubscription(subscription.toJSON());
  return subscription;
}
